import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  AfterViewInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PolicyService, UsersService } from '@horizon/core';
import { User } from '@horizon/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuRoutePaths, RoutePaths } from '@shared/enums/routes.enums';
import { EventFireService } from '@shared/services/event-fire.service';
import { AuthorizationService } from './services/authorization.service';
import { SearchApiService } from './services/search.service';
import { layout } from './config/layout';
import { UtilsService } from '@horizon/core';
import { first, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import { AppSectionsPermissionsKey } from '@shared/enums/permissions.enums';
import { TemplatesDataService } from '@shared/services/templates-data.service';
import {
  DialogNotification,
  NotificationService,
  ToastNotificationItem,
  ToastService,
} from '@horizon/core-notifications';
import { globalSettings } from 'src/global-settings';
import { HeaderConfigService } from './config/header-config.service';
import { SideBarMenuConfigService } from './config/side-bar-menu-config.service';
import { SharedService } from './services/shared.service';
import { HeaderToolbarOption } from '@horizon/core-layouts';
import { HeaderUsernameTileComponent } from './components/header-username-tile/header-username-tile.component';

@Component({
  selector: 'core',
  templateUrl: './core.component.html',
  styleUrls: ['./core.component.scss'],
})
export class CoreComponent implements OnInit, AfterViewInit, OnDestroy {
  public isOnAcceptDisclaimer: boolean = false;
  public isOnContactAdmin: boolean = false;
  private readonly destroy$ = new Subject<void>();

  /**
   * Dynamically inserted before Horizon avatar inside `.header-wrapper__actions-section--toolbar-wrapper`.
   * DOM/class names are coupled to @horizon/core-layouts. Accept-disclaimer/contact-admin use their own
   * horizon-app-header instances; add the same mount logic there only if those screens must show the name.
   */
  private usernameTileRef: ComponentRef<HeaderUsernameTileComponent> | null = null;
  private usernameTileMountAttempts = 0;
  private readonly usernameTileMountMaxAttempts = 30;

  public loginShortDisplayName: string = undefined;
  public canUserSearch: boolean = true;
  public currentLanguage: string;
  public user: User;
  public userPhoto: SafeResourceUrl | null = null;
  public userLoggedName: string = '';

  public isIframe = window !== window.parent && !window.opener;

  public toastNotifications: ToastNotificationItem[] = [];
  public notificationsDialog: DialogNotification;
  public showLoadingSpinner: boolean = false;
  public dataRecentsSearch: any;
  public globalSettings: any = globalSettings;
  public urlContainsTabelau: boolean = false;
  //Menu permissions enums
  public menuRoutingPaths: any = MenuRoutePaths;
  public menuItemsPermissions: any = AppSectionsPermissionsKey;

  recentSearchContent = this.utils.getLayoutElementById(
    layout,
    'recent-search'
  );
  public templateRecentSearch: any = {
    id: 'template',
    fields: [
      {
        name: 'anchored',
        type: 'icon',
      },
      {
        name: 'account',
        type: 'url',
      },
      {
        name: 'text',
      },
      {
        name: 'date',
        type: 'date',
      },
    ],
  };

  constructor(
    private readonly router: Router,
    public headerSettingsService: HeaderConfigService,
    private authService: MsalService,
    private authorizationService: AuthorizationService,
    private translate: TranslateService,
    private policyService: PolicyService,
    private usersService: UsersService,
    private domSanitizer: DomSanitizer,
    private eventFireService: EventFireService,
    public menuSettingsService: SideBarMenuConfigService,
    private readonly toastService: ToastService,
    private readonly notificationService: NotificationService,
    private host: ElementRef<HTMLElement>,
    private readonly utils: UtilsService,
    private searchApiService: SearchApiService,
    private location: Location,
    private templatesDataService: TemplatesDataService,
    private sharedService: SharedService,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly appRef: ApplicationRef,
    private readonly injector: Injector
  ) {
    this.menuSettingsService.setMenuColors(this.host);

    this.notificationService.getLoadingSpinner().subscribe((loading) => {
      this.showLoadingSpinner = loading;
    });

    this.toastService.getToastNotification().subscribe((notifications) => {
      this.toastNotifications = notifications;
    });

    this.notificationService
      .getNotificationDialog()
      .subscribe((notifications) => {
        this.notificationsDialog = notifications;
      });
  }

  ngOnInit(): void {
    this.currentLanguage = this.translate.currentLang;
    this.userLoggedName = localStorage.getItem('userLoggedName') || '';
    this.setLoginDisplay();
    const currentUrl = this.location.path();
    this.urlContainsTabelau = currentUrl.includes('tableau');
    this.isOnAcceptDisclaimer = currentUrl.includes('accept-disclaimer');
    this.isOnContactAdmin = currentUrl.includes('contact-admin');
  }

  ngAfterViewInit(): void {
    this.scheduleUsernameTileMount();
  }

  ngOnDestroy(): void {
    this.destroyUsernameTile();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public languageChanged(language: string): void {
    this.changeLanguage(language);
  }

  private changeLanguage(language: string): void {
    this.eventFireService.currentLanguageChanged(language);
    this.currentLanguage = language;
  }

  public goHome(): void {
    const element = document.querySelector(
      '#sidebar-treeview > ul > li:nth-child(1)'
    ) as HTMLElement;
    element?.click();
    this.sharedService.setFirstMenuItemAsSelected();
    this.router.navigate([RoutePaths.HOME]);
  }

  public logout(): void {
    this.authorizationService.logout();
  }

  /**
   * Same as clicking `horizon-user-avatar` — toggles `horizon-user-menu` / `.custom-menu-container`.
   * The username tile uses `stopPropagation` on its click so Horizon's document listener does not immediately close the menu.
   */
  public openUserMenuLikeAvatar(): void {
    const target = document.querySelector(
      'horizon-app-header horizon-user-avatar .horizon-user-avatar__photo'
    ) as HTMLElement | null;
    target?.click();
  }

  private setLoginDisplay(): void {
    const allAccounts = this.authService?.instance?.getAllAccounts();
    const localAccountId = allAccounts[0]?.localAccountId;

    this.fetchUser(localAccountId);
  }

  private fetchUser(userId): void {
    this.policyService.getMergedUser(userId).subscribe((res) => {
      this.user = res;
      if (this.userLoggedName) {
        this.user = {
          ...this.user,
          name: this.userLoggedName,
          adDisplayName: this.userLoggedName,
        };
      }
      this.changeLanguage(this.user.language ?? 'en');

      if (!res?.roleGuid || res?.roleGuid === "" || res?.roleGuid == undefined || res?.roleGuid == null) {
        this.sharedService.setUserHasNoConfig()
      }

      if (res?.userPhoto) {
        this.userPhoto = this.domSanitizer.bypassSecurityTrustResourceUrl(
          'data:image;base64,' + res.userPhoto
        );
      } else {
        this.user.userPhoto = null;
        const allAccounts = this.authService?.instance?.getAllAccounts();
        const loginDisplay = allAccounts?.length > 0;

        if (loginDisplay && allAccounts[0]?.name) {
          const splittedName = allAccounts[0]?.name.split(' ');
          this.loginShortDisplayName = splittedName[0][0] + splittedName[1][0];
        }
      }
      this.updateUsernameTile();
    });
  }

  private scheduleUsernameTileMount(): void {
    const attempt = (): void => {
      if (this.destroy$.closed) {
        return;
      }
      this.updateUsernameTile();
      if (this.usernameTileRef) {
        this.usernameTileMountAttempts = 0;
        return;
      }
      this.usernameTileMountAttempts++;
      if (this.usernameTileMountAttempts < this.usernameTileMountMaxAttempts) {
        setTimeout(attempt, 50);
      }
    };
    setTimeout(attempt, 0);
  }

  private tryMountUsernameTile(): boolean {
    if (this.usernameTileRef) {
      return true;
    }
    const toolbarWrapper = document.querySelector(
      'horizon-app-header .header-wrapper__actions-section--toolbar-wrapper'
    ) as HTMLElement | null;
    const avatarEl = toolbarWrapper?.querySelector(
      'horizon-user-avatar'
    ) as HTMLElement | null;
    const avatarWrapper = avatarEl?.parentElement;
    if (!toolbarWrapper || !avatarWrapper) {
      return false;
    }
    if (toolbarWrapper.querySelector('[data-cp-username-tile]')) {
      return false;
    }

    const factory = this.componentFactoryResolver.resolveComponentFactory(
      HeaderUsernameTileComponent
    );
    const ref = factory.create(this.injector);
    this.appRef.attachView(ref.hostView);
    const hostEl = ref.location.nativeElement as HTMLElement;
    toolbarWrapper.insertBefore(hostEl, avatarWrapper);
    this.usernameTileRef = ref;
    return true;
  }

  private updateUsernameTile(): void {
    if (!this.usernameTileRef && !this.tryMountUsernameTile()) {
      return;
    }
    if (!this.usernameTileRef) {
      return;
    }
    const name =
      this.userLoggedName || (this.user as { name?: string })?.name || '';
    this.usernameTileRef.instance.displayName = name;
    this.usernameTileRef.instance.menuActivator = () => this.openUserMenuLikeAvatar();
    this.usernameTileRef.changeDetectorRef.detectChanges();
  }

  private destroyUsernameTile(): void {
    if (this.usernameTileRef) {
      this.appRef.detachView(this.usernameTileRef.hostView);
      this.usernameTileRef.destroy();
      this.usernameTileRef = null;
    }
  }

  public confirmModal(): void {
    this.notificationService.confirmActionClicked();
  }

  public totalData() {
    this.searchApiService
      .getAccountsSearch()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        response.map((item) => {
          const newDate = new Date(item.lastSearchDate);
          item['lastSearchDate'] = newDate.toLocaleString();
        });
        this.dataRecentsSearch = response;
      });
  }

  public updateData(args) {
    args.pin = !args.pin;
    this.searchApiService
      .postAccountSearch(args)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.totalData();
      });
  }

  public selectItemClick(id): void {
    const found = this.dataRecentsSearch.find((_) => _.accountNumber === id);
    if (found) {
      this.searchApiService
        .postAccountSearch(found)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.totalData();
          this.router.navigate([RoutePaths.ACCOUNT, id]).then(() => {
            this.templatesDataService.setTemplates({});
            this.templatesDataService.setTemplatesReferences({});
            this.templatesDataService.setItemsByTemplates({});
          });
        });
    } else {
      this.router.navigate([RoutePaths.ACCOUNT, id]).then(() => {
        this.templatesDataService.setTemplates({});
        this.templatesDataService.setTemplatesReferences({});
        this.templatesDataService.setItemsByTemplates({});
      });
    }
  }

  public onSearch(event: any): void {
    if (event.type === 'advanced') {
      this.router.navigate([RoutePaths.SEARCH]);
    } else if (event.value >= 0) {
      this.router.navigate([RoutePaths.SEARCH, event.value]);
    }
  }

  handleItemClick(data: any) {
    if (data.nodeId) this.sharedService.setSelectedMenuItem(data);
  }

  public onToolbarItemClicked(event: HeaderToolbarOption): void {
    switch (event.action) {
      case 'window_open':
        this.onWindowOpenAction(event);
        break;
      default:
        break;
    }
  }

  public onWindowOpenAction(event: any): void {
    window.open(event.url, '_blank');
  }
}
